import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Delay before starting tour (ms) to wait for CSS layout animations.
 */
const TOUR_START_DELAY_MS = 500;

interface UseOnboardingTourArgs {
  /** Unique key for this page's tour (e.g. "dashboard", "grants-calls") */
  pageKey: string;
  /** Version number - increment when steps change to re-show tour */
  version: number;
  /** Current user ID, or null if not logged in */
  userId: string | null;
  /** Whether the page/context is still loading */
  isLoading: boolean;
}

interface UseOnboardingTourResult {
  /** Whether the tour should currently be running */
  shouldRunTour: boolean;
  /** Mark the tour as completed (saves to localStorage) */
  markTourCompleted: () => void;
  /** Reset tour completion (removes localStorage key, re-enables tour) */
  resetTour: () => void;
  /** The localStorage key used, or null if userId is null */
  storageKey: string | null;
}

/**
 * Build the localStorage key for onboarding tour state.
 * Format: onboarding-tour-{pageKey}-v{version}-{userId}
 */
function buildStorageKey(pageKey: string, version: number, userId: string): string {
  return `onboarding-tour-${pageKey}-v${version}-${userId}`;
}

/**
 * Hook managing onboarding tour lifecycle for a specific page.
 *
 * Follows the localStorage pattern from StatsBar (grants-stats-dismissed).
 * Guards: userId must be non-null, isLoading must be false,
 * and a 500ms delay after conditions are met (CSS animations).
 *
 * @example
 * const { shouldRunTour, markTourCompleted } = useOnboardingTour({
 *   pageKey: "dashboard",
 *   version: 1,
 *   userId: user?.id ?? null,
 *   isLoading,
 * });
 */
export function useOnboardingTour({
  pageKey,
  version,
  userId,
  isLoading,
}: UseOnboardingTourArgs): UseOnboardingTourResult {
  const [shouldRunTour, setShouldRunTour] = useState(false);
  const [resetCount, setResetCount] = useState(0);
  const shouldRunTourRef = useRef(false);

  const storageKey = userId ? buildStorageKey(pageKey, version, userId) : null;

  // Keep ref in sync for use in unmount cleanup
  useEffect(() => {
    shouldRunTourRef.current = shouldRunTour;
  }, [shouldRunTour]);

  useEffect(() => {
    // Guard: no user or still loading
    if (!userId || isLoading || !storageKey) {
      setShouldRunTour(false);
      return;
    }

    // Check localStorage - already completed?
    try {
      const completed = localStorage.getItem(storageKey);
      if (completed) {
        setShouldRunTour(false);
        return;
      }
    } catch {
      // localStorage not available - skip tour
      setShouldRunTour(false);
      return;
    }

    // Wait for CSS layout animations before starting tour
    const timer = setTimeout(() => {
      setShouldRunTour(true);
    }, TOUR_START_DELAY_MS);

    return () => clearTimeout(timer);
  }, [userId, isLoading, storageKey, resetCount]);

  // On unmount: if tour was running (user navigated away), mark as completed
  useEffect(() => {
    const key = storageKey;
    return () => {
      if (shouldRunTourRef.current && key) {
        try {
          localStorage.setItem(key, "done");
        } catch {
          // ignore
        }
      }
    };
  }, [storageKey]);

  const markTourCompleted = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, "done");
    } catch (e) {
      console.warn("localStorage write error:", e);
    }
    setShouldRunTour(false);
  }, [storageKey]);

  const resetTour = useCallback(() => {
    if (!storageKey) return;
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {
      console.warn("localStorage remove error:", e);
    }
    setShouldRunTour(false);
    // Trigger re-evaluation of the effect
    setResetCount((c) => c + 1);
  }, [storageKey]);

  return {
    shouldRunTour,
    markTourCompleted,
    resetTour,
    storageKey,
  };
}
